<script lang="ts">
  import type { AdminCapability } from "$lib/api/contract";
  import Button from "$lib/components/Button.svelte";

  interface Props {
    capability: AdminCapability;
  }
  const { capability }: Props = $props();
</script>

<!--
  Deliberately rendered inside AdminShell so navigation and account controls stay
  where they were. The copy is uniform for every denied capability and every target:
  it says only that this account lacks the operation, never whether the thing behind
  the route exists.
-->
<section class="denied" aria-labelledby="capability-denied-title">
  <p class="eyebrow">Support console</p>
  <h1 class="page-title" id="capability-denied-title" data-page-title tabindex="-1">
    Page unavailable
  </h1>
  <p class="lead">
    This support account doesn't have access to that operation. Nothing about it is
    shown here.
  </p>
  <dl class="kv detail">
    <div>
      <dt>Required capability</dt>
      <dd><span class="mono">{capability}</span></dd>
    </div>
  </dl>
  <p class="note">
    If you need it, ask an EpiDev administrator to grant the capability to your account.
  </p>
  <div class="actions">
    <Button size="sm" variant="ghost" href="/admin/entitlements">Back to customers</Button>
  </div>
</section>

<style>
  .denied {
    display: grid;
    gap: var(--space-3);
    align-content: start;
    justify-items: start;
    width: 100%;
    max-width: 62ch;
    min-width: 0;
    padding: var(--space-8) clamp(16px, 2.4vw, 32px);
  }
  .eyebrow {
    margin: 0;
  }
  .page-title {
    margin: 0;
    color: var(--heading);
    font-size: 24px;
    letter-spacing: 0;
  }
  .lead,
  .note {
    margin: 0;
    color: var(--muted);
    font-size: 14px;
    line-height: 1.6;
  }
  .note {
    font-size: 13px;
  }
  /* One quiet fact between hairlines rather than a boxed callout. */
  .detail {
    width: 100%;
    margin: var(--space-2) 0;
    padding: var(--space-3) 0;
    border-top: 1px solid var(--line-soft);
    border-bottom: 1px solid var(--line-soft);
  }
  .detail > div {
    grid-template-columns: auto minmax(0, 1fr);
    gap: 16px;
  }
  .mono {
    color: var(--heading);
    font-family: var(--font-mono);
    font-size: 13px;
  }
  .actions {
    margin-top: var(--space-2);
  }
</style>
